import * as React from "react"

import { sendCommand } from "./api"
import { domainFromUrl } from "./format"

export interface LiveDomainState {
  url: string | null
  domain: string | null
  /** האם התקבלה כבר תשובה כלשהי מהמכשיר - גם `url: null` היא תשובה תקפה */
  loaded: boolean
  loading: boolean
  /** הניסיון האחרון נכשל (timeout / מכשיר שלא תומך בפקודה) */
  error: boolean
  /**
   * למכשיר אין Activity רשומה. זה מצב שונה לגמרי מ"אין עמוד טעון": הפקודות
   * שתלויות ב-WebView (רענון, כתובת) ייכשלו עד שהאפליקציה תופעל מחדש.
   */
  noActivity: boolean
  updatedAt: number | null
}

const EMPTY: LiveDomainState = {
  url: null,
  domain: null,
  loaded: false,
  loading: false,
  error: false,
  noActivity: false,
  updatedAt: null,
}

// התשאול חוזר כל 30 שניות, אז אין טעם להמתין לתגובה יותר מזה. ה-timeout של השרת
// הוא 15 שניות; כאן קוצבים קצת פחות כדי שהמצב בממשק יתעדכן ולא ייתקע על "טוען".
const REQUEST_TIMEOUT_MS = 8000

/**
 * תשאול חי של הכתובת המוצגת כעת על מסך המכשיר (פקודת get_current_url).
 * מתשאל כל intervalMs כל עוד המכשיר מחובר; עוצר כשהוא מנותק או כשהטאב מוסתר.
 */
export function useLiveDomain(
  deviceId: string,
  online: boolean,
  intervalMs = 5000
): LiveDomainState & { refresh: () => void } {
  const [state, setState] = React.useState<LiveDomainState>(EMPTY)
  const inFlight = React.useRef(false)

  const fetchOnce = React.useCallback(async () => {
    if (inFlight.current) return
    inFlight.current = true
    setState((s) => ({ ...s, loading: true }))
    try {
      const data = await sendCommand<{ url?: string; hasActivity?: boolean } | string>(
        deviceId,
        "get_current_url",
        undefined,
        { signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS) },
      )
      const url = (typeof data === "string" ? data : data?.url) || null
      // גרסאות אפליקציה ישנות לא מחזירות hasActivity - אז היעדרו אינו תקלה
      const noActivity = typeof data === "object" && data?.hasActivity === false
      setState({
        url,
        domain: domainFromUrl(url),
        loaded: true,
        loading: false,
        error: false,
        noActivity,
        updatedAt: Date.now(),
      })
    } catch {
      // שומרים את הערך האחרון שנקרא - עדיף להציג אותו עם חותמת זמן מאשר לרוקן את הכרטיס
      setState((s) => ({ ...s, loading: false, error: true }))
    } finally {
      inFlight.current = false
    }
  }, [deviceId])

  React.useEffect(() => {
    if (!online) {
      setState(EMPTY)
      return
    }
    let cancelled = false
    const tick = () => {
      if (cancelled) return
      if (document.visibilityState === "hidden") return
      fetchOnce()
    }
    tick()
    const handle = window.setInterval(tick, intervalMs)
    const onVisible = () => {
      if (document.visibilityState === "visible") tick()
    }
    document.addEventListener("visibilitychange", onVisible)
    return () => {
      cancelled = true
      window.clearInterval(handle)
      document.removeEventListener("visibilitychange", onVisible)
    }
  }, [deviceId, online, intervalMs, fetchOnce])

  return { ...state, refresh: fetchOnce }
}
